import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Search, ExternalLink, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Input } from "@/components/ui/input";
import PositListCard from "@/components/posit/PositListCard";

type SavedPosit = {
  id: string;
  posit_id: string;
  created_at: string;
  posit: {
    id: string;
    title: string;
    public_description?: string | null;
    posit_description?: string | null;
    is_monetized?: boolean | null;
    subscription_price?: number | null;
    users: { id: string; first_name: string | null; last_name: string | null; handle: string };
  };
};

const DashboardSubscriptionManagement: React.FC = () => {
  const [savedPosits, setSavedPosits] = useState<SavedPosit[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) { fetchSavedPosits(); }
  }, [user]);

  const fetchSavedPosits = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('saved_posits')
        .select(`
          id, posit_id, created_at,
          posit:posits (
            id, title, public_description, posit_description, is_monetized, subscription_price,
            users!fk_posits_user_id ( id, first_name, last_name, handle )
          )
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSavedPosits((data as any) || []);
    } catch (e) {
      // silent fail
    } finally { setLoading(false); }
  };

  const handleUnsave = async (positId: string) => {
    if (!user?.id) return;
    try {
      const { error } = await supabase
        .from('saved_posits')
        .delete()
        .eq('user_id', user.id)
        .eq('posit_id', positId);
      if (error) throw error;
      setSavedPosits(prev => prev.filter(s => s.posit_id !== positId));
    } catch (e) {
      toast({ title: "Couldn’t unsave", description: "Please try again.", variant: "destructive" });
    }
  };

  const getCreatorName = (user: any) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.handle || 'Anonymous';
  };

  const getCreatorInitials = (user: any) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user.handle ? user.handle[0].toUpperCase() : 'A';
  };

  if (loading) {
    return (
      <DashboardLayout
        title="Saved Posits"
        description="Your saved Posits"
      >
        <div className="space-y-6">
          <div className="grid gap-3 grid-cols-1 md:grid-cols-3">
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
          <div className="space-y-4">
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="h-48 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Saved Posits"
      description="Your saved Posits"
      maxWidth="2xl"
    >
      {/* Search and Filters */}
      <div className="mb-6 space-y-4 sticky top-0 z-10 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-2">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search saved posits..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Combined List */}
      <div className="space-y-3">
        {savedPosits.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Star className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-semibold mb-2">No Saved Posits Yet</h3>
              <p className="text-muted-foreground mb-6">Explore Posits to find ones to save.</p>
              <Button asChild><Link to="/public-posits"><ExternalLink className="w-4 h-4 mr-2" />Explore Posits</Link></Button>
            </CardContent>
          </Card>
        ) : null}

        {/* Saved Posits */}
        {savedPosits
          .filter(s => !searchQuery || s.posit.title.toLowerCase().includes(searchQuery.toLowerCase()) || (s.posit.users.handle || '').toLowerCase().includes(searchQuery.toLowerCase()))
          .map((saved) => (
          <PositListCard
            key={`saved-${saved.id}`}
            posit={saved.posit as any}
            creatorHandle={saved.posit.users?.handle}
            creatorName={getCreatorName(saved.posit.users || {})}
            showCreatorButton={false}
            showEditButton={false}
            showShareButton={false}
            showWikiButton={false}
            variant="compact"
          />
        ))}
      </div>
    </DashboardLayout>
  );
};

export default DashboardSubscriptionManagement; 